import { Box, Typography } from "@mui/material"
import type { ReactElement } from "react"
import { useMemo, useState } from "react"
import { useFormContext } from "react-hook-form"

import AutoComplete from "@/components/common/Autocomplete/AutoComplete"
import ValidationAdornment from "@/components/common/Autocomplete/ValidationAdornment"
import type { Category } from "@/graphql/codegen/graphql"
import useDuplicateByFieldValidation from "@/hooks/useDuplicateByFieldValidation"
import useProductCategory from "@/screens/ManageProducts/components/ProductCategories/hooks/useProductCategory"
import useDebounce from "@/utils/useDebounce"

interface ProductCategoryFormProps {
  originalName?: string
  onSubmit: () => void
}

const ProductCategoryForm = ({ onSubmit, originalName }: ProductCategoryFormProps): ReactElement => {
  const { control } = useFormContext()
  const isCreateMode = !originalName
  const [query, setQuery] = useState("")
  const debouncedName = useDebounce(query, 300)
  const shouldQuery = debouncedName.length >= 2

  const { productCategoryList } = useProductCategory({
    query: debouncedName,
    pause: !shouldQuery,
  })

  const productCategoryNameList = useMemo(() => {
    if (!shouldQuery) {
      return []
    }
    return productCategoryList.map((category) => category.name)
  }, [productCategoryList, shouldQuery])

  useDuplicateByFieldValidation<Category>({
    query,
    list: productCategoryList,
    fieldName: "name",
    fieldLabel: "name",
    entityName: "Category",
  })

  const handleCategoryNameChange = (value: string) => {
    setQuery(value)
  }

  return (
    <form onSubmit={onSubmit} className='space-y-8'>
      {isCreateMode && (
        <Typography className='text-ml text-gray-900' data-testid='category-dialog-title'>
          Create a new product category
        </Typography>
      )}
      <Box>
        <AutoComplete
          onInputChange={handleCategoryNameChange}
          label='Category Name'
          placeholder='Category Name'
          id='category-name-field'
          options={productCategoryNameList}
          name='name'
          control={control}
          defaultEmptyState=''
          freeSolo
          listSelectionDisabled
          EndAdornment={query ? ValidationAdornment : undefined}
          required
          data-testid='category-name-field'
        />
      </Box>
    </form>
  )
}

export default ProductCategoryForm
